/**
 * A diagnostic split into the parts the reader scans for.
 */
export interface Diagnostic {
  /** The renderer's error code, when it named one. */
  code?: string;
  /** Where in the template the problem is, when the renderer located it. */
  position?: string;
  /** What is wrong, in the renderer's own words. */
  message: string;
}

/**
 * The shape the renderer reports a template problem in, for example
 * `XPST0017 at line 15, column 98: Cannot find a 0-argument function`.
 *
 * The code is an upper-case identifier, the position counts from `at` up to the
 * colon that ends the header, and everything after it is the message. Only the
 * code is required to treat the header as one, since FOP reports plenty of
 * problems it cannot locate.
 */
const diagnosticPattern = /^([A-Z][A-Z0-9]{3,})(?:\s+at\s+([^:]+?))?:\s*(.+)$/s;

/**
 * Separates a diagnostic into its code, its position and its message so a
 * reader can scan the positions of a list rather than read every sentence.
 *
 * A line that does not carry that shape is its own message, which is what FOP
 * reports for anything it cannot attribute to a place in the template.
 */
export const parseDiagnostic = (line: string): Diagnostic => {
  const match = diagnosticPattern.exec(line.trim());

  if (match === null) {
    return { message: line };
  }

  const [, code, position, message] = match;

  return {
    code,
    position: position === undefined ? undefined : position.trim(),
    message: message.trim(),
  };
};
